import { useEffect, useState } from 'react'
import api from '../../../../helpers/apiRequester'
import { useMhlAccess } from './useMhlAccess'

export interface MhlScope {
    /** Full visibility: Sentinel staff and MHL admins. */
    all: boolean
    teamId: number | null
    teamName: string | null
    isTeamLeader: boolean
    /** Team leaders may see onboarding stats / overview for their team. Plain agents may not. */
    canViewStats: boolean
}

const FULL: MhlScope = { all: true, teamId: null, teamName: null, isTeamLeader: false, canViewStats: true }

// One fetch per session, shared by every consumer (sidebar, pages).
let cached: MhlScope | null = null
let inflight: Promise<MhlScope> | null = null
const listeners = new Set<(s: MhlScope) => void>()

const fetchScope = (): Promise<MhlScope> => {
    if (cached) return Promise.resolve(cached)
    if (!inflight) {
        inflight = api({ url: '/military-hearing-loss-scope', data: {} })
            .then((res) => {
                const s = res.data?.scope
                const scope: MhlScope = s
                    ? {
                          all: !!s.all,
                          teamId: s.teamId ?? null,
                          teamName: s.teamName ?? null,
                          isTeamLeader: !!s.isTeamLeader,
                          canViewStats: !!s.canViewStats,
                      }
                    : { ...FULL, all: false, canViewStats: false }
                cached = scope
                listeners.forEach((l) => l(scope))
                return scope
            })
            .catch(() => {
                // Fail closed: no stats, own clients only. Retry on next mount.
                inflight = null
                return { all: false, teamId: null, teamName: null, isTeamLeader: false, canViewStats: false }
            })
    }
    return inflight
}

/** Drop the cache (e.g. after team changes) so the next consumer refetches. */
export const invalidateMhlScope = () => {
    cached = null
    inflight = null
}

/**
 * The server-declared MHL visibility scope for the current user. Admins and
 * Sentinel staff resolve immediately without a request.
 */
export const useMhlScope = () => {
    const { isMhlAdmin, isMhlUser } = useMhlAccess()
    const [scope, setScope] = useState<MhlScope | null>(isMhlAdmin ? FULL : cached)

    useEffect(() => {
        if (isMhlAdmin) {
            setScope(FULL)
            return
        }
        if (!isMhlUser) {
            setScope(null)
            return
        }
        let active = true
        const onUpdate = (s: MhlScope) => active && setScope(s)
        listeners.add(onUpdate)
        void fetchScope().then(onUpdate)
        return () => {
            active = false
            listeners.delete(onUpdate)
        }
    }, [isMhlAdmin, isMhlUser])

    return { scope, loading: scope === null && isMhlUser }
}
